import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon, Menu } from '@percent/lemonade'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { GivingCampaignStatusDto } from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { useGivingCampaignActions } from './useGivingCampaignActions'

export type ActionsMenuProps = Readonly<{
  campaignId: string
  campaignHref: string
  campaignEditHref: string
  campaignTitle: string
  campaignStatus: GivingCampaignStatusDto
  campaignEndDate: string | null
}>

export function ActionsMenu({
  campaignId,
  campaignHref,
  campaignEditHref,
  campaignTitle,
  campaignStatus,
  campaignEndDate
}: ActionsMenuProps) {
  const { t } = useTranslation()
  const { userCan } = usePermissionCheck()

  const items = useMemo(
    () => [
      {
        key: 'view',
        label: t('button.view')
      },
      ...(campaignStatus !== GivingCampaignStatusDto.CANCELLED && campaignEndDate && userCan('manageGivingCampaigns')
        ? [
            {
              key: 'edit',
              label: t('button.edit')
            }
          ]
        : []),
      ...(campaignStatus === GivingCampaignStatusDto.PUBLISHED && userCan('manageGivingCampaigns')
        ? [
            {
              key: 'unpublish',
              label: t('button.unpublish')
            }
          ]
        : []),
      ...(campaignStatus === GivingCampaignStatusDto.DRAFT && userCan('manageGivingCampaigns')
        ? [
            {
              key: 'publish',
              label: t('button.publish')
            }
          ]
        : []),
      ...(campaignStatus !== GivingCampaignStatusDto.CANCELLED && userCan('manageGivingCampaigns')
        ? [
            {
              key: 'delete',
              label: t('button.cancel')
            }
          ]
        : [])
    ],
    [t, userCan, campaignStatus, campaignEndDate]
  )

  const {
    view,
    edit,
    publish,
    unpublish,
    delete: del
  } = useGivingCampaignActions({
    campaignId,
    campaignHref,
    campaignEditHref,
    campaignTitle,
    campaignStatus
  })

  const onSelect = (key: string) => {
    switch (key) {
      case 'view':
        view()
        break
      case 'edit':
        edit()
        break
      case 'publish':
        publish()
        break
      case 'unpublish':
        unpublish()
        break
      case 'delete':
        del()
        break
      default:
    }
  }

  return <Menu title={<Icon name="menu-vertical" size={6} />} onSelect={onSelect} sections={[{ items }]} />
}
